// job-web-main/frontend/src/components/dashboard/VisitBookingWidget.js
import { useState } from 'react';
import { CalendarPlus, Mail, Loader2 } from 'lucide-react';
import { bookVisit, fetchVisitIcsUrl, portalErrorMessage } from '../../api/portal';
import './dashboardTabs.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WINDOWS = [
  { key: 'morning', label: 'Morning (8–11)' },
  { key: 'midday', label: 'Midday (11–2)' },
  { key: 'afternoon', label: 'Afternoon (2–5)' },
];

function fmtDate(d) { return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`; }
function isSunday(d) { return d.getDay() === 0; }
function nextBusinessDay(from) {
  const d = new Date(from);
  d.setDate(d.getDate() + 1);
  while (isSunday(d)) d.setDate(d.getDate() + 1);
  return d;
}
function toDateInputValue(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Reused across every stage that needs a visit (office visit today; offer-letter/visa signing are
 * a separate ops-module mechanism, not yet wired to this — see spec §11). `onBooked(result)` fires
 * with the API response so the parent can refresh its own status. */
export default function VisitBookingWidget({ mode, officeVisitDate, address, phone, onBooked }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [date, setDate] = useState(() => toDateInputValue(nextBusinessDay(new Date())));
  const [windowKey, setWindowKey] = useState('midday');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [calBusy, setCalBusy] = useState(false);

  const tomorrow = nextBusinessDay(new Date());

  async function handleBook(bookDate, bookWindow) {
    setBusy(true);
    setError('');
    try {
      const result = await bookVisit(bookDate, bookWindow);
      onBooked(result);
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not book that visit — please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleAddToCalendar() {
    setCalBusy(true);
    try {
      const url = await fetchVisitIcsUrl();
      const a = document.createElement('a');
      a.href = url;
      a.download = 'adel-office-visit.ics';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not create the calendar file — please try again.'));
    } finally {
      setCalBusy(false);
    }
  }

  if (mode === 'confirmed' && officeVisitDate) {
    const d = new Date(officeVisitDate);
    return (
      <div>
        {error && <div className="apply-error" role="alert" style={{ marginBottom: 10 }}>{error}</div>}
        <p style={{ fontSize: 13, color: 'var(--text-grey)', margin: '0 0 4px' }}>{fmtDate(d)}</p>
        <p style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.5, margin: '0 0 4px' }}>{address}. Bring your original passport.</p>
        {phone && <p style={{ fontSize: 12, color: 'var(--text-grey)', margin: '0 0 12px' }}>Questions? Call us: {phone}</p>}
        <div style={{ marginTop: 4 }}>
          <button type="button" className="dtab-assist-btn" disabled={calBusy} onClick={handleAddToCalendar} style={{ width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 7 }}>
            {calBusy ? <Loader2 size={15} className="spin" /> : <CalendarPlus size={15} />} Add to calendar
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, fontSize: 11.5, lineHeight: 1.55, color: 'var(--text-grey)' }}>
            <Mail size={13} style={{ flex: '0 0 auto', marginTop: 1 }} />
            {/* Wording matters here: the ERP now caps visit reminders at TWO PER CANDIDATE for good
                (candidateVisitReminderCron MAX_REMINDERS_PER_CANDIDATE), so someone who has already had
                both and then uses "Change your visit time" below will NOT get another pair. Promising two
                unconditionally, directly above that button, would be a promise we no longer keep. */}
            <span>We&apos;ll email you up to two reminders about your visit &mdash; and the confirmation above always shows your latest time.</span>
          </div>
        </div>
        <button type="button" className="apply-link-btn" style={{ marginTop: 10, padding: '.4rem 0' }} onClick={() => setPickerOpen((v) => !v)}>
          Change your visit time
        </button>
        {pickerOpen && (
          <div style={{ marginTop: 10, paddingTop: 12, borderTop: '1px dashed var(--border-light)' }}>
            {renderPicker()}
          </div>
        )}
      </div>
    );
  }

  function renderPicker() {
    return (
      <>
        <label className="apply-label" htmlFor="visit-date" style={{ fontSize: 11, marginBottom: 5 }}>Pick a date</label>
        <input id="visit-date" type="date" className="apply-input" style={{ marginBottom: 10 }}
          value={date} min={toDateInputValue(tomorrow)}
          onChange={(e) => setDate(e.target.value)} />
        <label className="apply-label" style={{ fontSize: 11, marginBottom: 5 }}>Pick a window</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {WINDOWS.map((w) => (
            <button key={w.key} type="button" onClick={() => setWindowKey(w.key)}
              className="dtab-windowbtn" style={{
                flex: 1, fontSize: 12.5, fontWeight: 700, padding: '9px 6px', borderRadius: 9, cursor: 'pointer',
                background: windowKey === w.key ? 'var(--brand-navy)' : 'var(--bg-white)',
                color: windowKey === w.key ? 'var(--text-light)' : 'var(--text-dark)',
                border: `1.5px solid ${windowKey === w.key ? 'var(--brand-navy)' : 'var(--border-medium)'}`,
              }}>
              {w.label}
            </button>
          ))}
        </div>
        <button type="button" className="apply-btn" disabled={busy || !date} onClick={() => handleBook(date, windowKey)}>
          {busy ? <Loader2 size={16} className="spin" /> : null} Confirm visit
        </button>
      </>
    );
  }

  return (
    <div>
      {error && <div className="apply-error" role="alert" style={{ marginBottom: 10 }}>{error}</div>}
      <div style={{ background: '#FBF3E9', border: '1px dashed #F0C48C', borderRadius: 12, padding: '13px 14px', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#B08340', margin: '0 0 3px' }}>Earliest available</p>
            <p style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>Tomorrow</p>
            <p style={{ fontSize: 12, color: '#8A7A5C', margin: '2px 0 0' }}>{fmtDate(tomorrow)} · Morning (8–11am)</p>
          </div>
          <button type="button" className="apply-btn" disabled={busy} style={{ width: 'auto', margin: 0, padding: '9px 13px' }}
            onClick={() => handleBook(toDateInputValue(tomorrow), 'morning')}>
            {busy ? <Loader2 size={15} className="spin" /> : null} Book tomorrow
          </button>
        </div>
      </div>
      <button type="button" className="apply-link-btn" style={{ padding: '.4rem 0 0' }} onClick={() => setPickerOpen((v) => !v)}>
        {pickerOpen ? 'Hide date picker' : 'Choose a different date instead'}
      </button>
      {pickerOpen && (
        <div style={{ marginTop: 10, paddingTop: 12, borderTop: '1px dashed var(--border-light)' }}>
          {renderPicker()}
        </div>
      )}
    </div>
  );
}
